import React from 'react'
import { useTranslation } from 'react-i18next'
import PriceCard from '../../molecule/priceCard/index.tsx'
import { getPaymentLink } from '../../../helpers/paymentHelper.ts'
import { trackMarketingEvent } from '../../../helpers/analytics.ts'
import { ADDITIONAL_PRICE_DATE, PUBLIC_PRICE_CATALOG_PATH, WEDDING_CAMERA_PRICES } from '../../../constants/pricing.ts'
import type { SupportedLanguage } from '../../../types/i18n'
import './index.css'

interface Props {
	title?: string
	headingLevel?: 1 | 2
}

const PriceSection: React.FC<Props> = ({
	title,
	headingLevel = 2
}) => {
	const { t, i18n } = useTranslation('pricing')
	const { t: tCommon } = useTranslation()
	const currentLanguage = i18n.language as SupportedLanguage

	const displayTitle = title || t('title')

	const basicFeatures = t('basic.features', { returnObjects: true }) as string[]
	const premiumFeatures = t('premium.features', { returnObjects: true }) as string[]
	const exclusiveFeatures = t('exclusive.features', { returnObjects: true }) as string[]
	const currentPriceLabel = t('currentPriceLabel')
	const anchorPriceLabel = t('anchorPriceLabel')
	const anchorPriceTooltip = t('anchorPriceTooltip')

	return (
		<section className='price-section'>
			<div className='price-section-title-container'>
				{headingLevel === 1
					? <h1 className='price-section-title'>{displayTitle}</h1>
					: <h2 className='price-section-title'>{displayTitle}</h2>}
			</div>

			<div className='cards-container'>
				<PriceCard
					price={WEDDING_CAMERA_PRICES.basic.currentPrice}
					anchorPrice={WEDDING_CAMERA_PRICES.basic.anchorPrice}
					currentPriceLabel={currentPriceLabel}
					anchorPriceLabel={anchorPriceLabel}
					anchorPriceTooltip={anchorPriceTooltip}
					cardStyle='secondary'
					title={t('basic.title')}
					info={Array.isArray(basicFeatures) ? basicFeatures : []}
					btnTitle={tCommon('buttons.reserve')}
					onClick={() => {
						trackMarketingEvent('checkout_click', 'basic')
						window.location.href = getPaymentLink('basic', currentLanguage)
					}}
				/>
				<PriceCard
					price={WEDDING_CAMERA_PRICES.premium.currentPrice}
					anchorPrice={WEDDING_CAMERA_PRICES.premium.anchorPrice}
					currentPriceLabel={currentPriceLabel}
					anchorPriceLabel={anchorPriceLabel}
					anchorPriceTooltip={anchorPriceTooltip}
					cardStyle='primary'
					title={t('premium.title')}
					info={Array.isArray(premiumFeatures) ? premiumFeatures : []}
					btnTitle={tCommon('buttons.reserve')}
					onClick={() => {
						trackMarketingEvent('checkout_click', 'premium')
						window.location.href = getPaymentLink('premium', currentLanguage)
					}}
				/>
				<PriceCard
					price={WEDDING_CAMERA_PRICES.exclusive.currentPrice}
					anchorPrice={WEDDING_CAMERA_PRICES.exclusive.anchorPrice}
					currentPriceLabel={currentPriceLabel}
					anchorPriceLabel={anchorPriceLabel}
					anchorPriceTooltip={anchorPriceTooltip}
					cardStyle='secondary'
					title={t('exclusive.title')}
					info={Array.isArray(exclusiveFeatures) ? exclusiveFeatures : []}
					btnTitle={tCommon('buttons.reserve')}
					onClick={() => {
						trackMarketingEvent('checkout_click', 'exclusive')
						window.location.href = getPaymentLink('exclusive', currentLanguage)
					}}
				/>
			</div>
			<p className='price-legal-notice' data-additional-price-date={ADDITIONAL_PRICE_DATE}>{t('anchorPriceNotice')}</p>
			<a className='price-catalog-link' href={PUBLIC_PRICE_CATALOG_PATH} target='_blank' rel='noreferrer'>
				{t('priceCatalogLinkLabel')}
			</a>
		</section>
	)
}

export default PriceSection
